import React from 'react';
import styled, { css } from 'styled-components';
import { CONTENT } from '../content';
import {
  AboutCopy,
  ArtistCopy,
  Bolt,
  Buy,
  C,
  COMIC_PAGES,
  ContractAddress,
  CtoCopy,
  DrawnFooter,
  DrawnNav,
  EASE_OUT,
  InkCheck,
  InkRing,
  MEMES_12,
  NDHAUS_PHOTO,
  ORIGINAL_ART,
  Fold,
  FoldSummary,
  ROADMAP_BLOCKS,
  RoadmapStop,
  RedesignFonts,
  Socials,
  Sparkle,
  SupplyDonut,
  TITLES,
  TOKEN_FACTS,
  TitleArt,
  VIDEOS,
  Video,
  hover,
} from '../kit';

/**
 * E, "ndhaus's sketchbook". The whole page is one sheet of blue squared paper. Every
 * picture is taped onto it, every heading is the title artwork stuck on the page, and the
 * only structure is what a hand would draw: a margin line, arrows, a highlighter swipe,
 * scribbled rings. No boxes, no panels.
 */

const tilt = (i: number) => [-3, 2.5, -1.5, 3.5, -2.5, 1.5][i % 6];

const renderNote = ({ milestone, lines, pill }: RoadmapStop) => {
  const full = lines.join(' ');
  const cut = full.indexOf(':') + 1;
  const date = cut > 0 ? full.slice(0, cut) : full;
  const title = cut > 0 ? full.slice(cut).trim() : '';
  return (
    <li key={milestone.id} className={milestone.status}>
      <div className="date">
        <span>{date}</span>
        <InkRing className="ring" color={milestone.status === 'planned' ? C.ink : C.pink} />
      </div>
      <div className="note">
        {title && <h3> {title}</h3>}
        {pill && <span className="pill">{pill}</span>}
        <p>{milestone.description}</p>
        {milestone.link && (
          <a href={milestone.link.href} target="_blank" rel="noopener noreferrer">
            {milestone.link.label}
          </a>
        )}
      </div>
    </li>
  );
};

const RedesignE: React.FC = () => (
  <Sheet data-design="e" id="top">
    <RedesignFonts />
    <DrawnNav color={C.ink} accent={C.yellow} />
    <Margin aria-hidden="true">
      <Sparkle size={30} color={C.yellow} style={{ top: '9%' }} />
      <Bolt size={34} style={{ top: '21%' }} />
      <Sparkle size={22} color={C.pink} style={{ top: '34%' }} />
      <svg viewBox="0 0 60 60" style={{ top: '46%' }}>
        <path d="M30 6 C 44 6, 54 18, 52 32 C 50 48, 30 56, 16 46 C 4 36, 10 16, 28 12" fill="none" stroke={C.ink} strokeWidth="3" strokeLinecap="round" />
      </svg>
      <Bolt size={28} style={{ top: '60%' }} />
      <Sparkle size={26} color={C.yellow} style={{ top: '73%' }} />
      <svg viewBox="0 0 60 60" style={{ top: '86%' }}>
        <path d="M8 40 C 18 10, 30 50, 40 20 S 52 30, 54 14" fill="none" stroke={C.pink} strokeWidth="4" strokeLinecap="round" />
      </svg>
    </Margin>

    <Hero>
      <div className="zeus">
        <img src="/redesign2/scenes/zeus-big.webp" alt="Zeus waving" />
        <Sparkle className="sp1" size={36} color={C.yellow} />
        <Sparkle className="sp2" size={24} color={C.pink} />
      </div>
      <div className="copy">
        <h1>
          <Swipe aria-hidden="true" />
          <TitleArt src={TITLES.zeus.src} alt={TITLES.zeus.alt} $w="min(560px, 86vw)" $tilt={-4} />
        </h1>
        <Arrow aria-hidden="true" viewBox="0 0 160 90">
          <path d="M6 14 C 50 4, 110 20, 128 70" fill="none" stroke={C.ink} strokeWidth="4" strokeLinecap="round" />
          <path d="M112 62 L 130 76 L 140 54" fill="none" stroke={C.ink} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        </Arrow>
        <div className="buy">
          <Buy big />
        </div>
        <ContractAddress tone="dark" />
      </div>
    </Hero>

    <Spread id="about">
      <div className="text">
        <h2>
          <TitleArt src={TITLES.pepesDog.src} alt={TITLES.pepesDog.alt} $w="min(280px, 62vw)" $tilt={-5} />
        </h2>
        <Notes>
          <AboutCopy />
        </Notes>
      </div>
      <Taped $tilt={4} className="art">
        <img className="media" src={ORIGINAL_ART} alt="The Happiness of Zeus, the original artwork by ndhaus" />
        <Bolt className="bolt" size={46} />
      </Taped>
    </Spread>

    <Taped $tilt={-1.5} className="wide">
      <Video video={VIDEOS.trailer} className="media" />
    </Taped>

    <Spread $flip>
      <div className="text">
        <h2>
          <TitleArt src={TITLES.takeOver.src} alt={TITLES.takeOver.alt} $w="min(340px, 74vw)" $tilt={3} />
        </h2>
        <Notes>
          <CtoCopy />
        </Notes>
      </div>
      <Taped $tilt={-3} className="art">
        <Video video={VIDEOS.buy} className="media" />
      </Taped>
    </Spread>

    <Page id="tokenomics">
      <h2 className="center">
        <TitleArt src={TITLES.tokenomics.src} alt={TITLES.tokenomics.alt} $w="min(700px, 92vw)" $tilt={-2} />
      </h2>
      <Checklist>
        {TOKEN_FACTS.map((f, i) => (
          <li key={f.title} style={{ transform: `rotate(${tilt(i) / 3}deg)` }}>
            <a href={f.link} target="_blank" rel="noopener noreferrer" className="ink-host">
              <span className="box">
                <InkCheck size={30} />
              </span>
              <span>
                <span className="t">{f.title}</span>
                <span className="v">{f.text}</span>
              </span>
            </a>
          </li>
        ))}
      </Checklist>
      <p className="note center">{CONTENT.tokenomics.verifyText}</p>
      <div className="center">
        <SupplyDonut size={360} />
      </div>
    </Page>

    <Page id="roadmap">
      <h2 className="center">
        <TitleArt src={TITLES.roadmap.src} alt={TITLES.roadmap.alt} $w="min(480px, 86vw)" $tilt={2} />
      </h2>
      <Timeline>
        {ROADMAP_BLOCKS.map((block) =>
          block.kind === 'fold' ? (
            <li key={block.id} className="fold">
              <Fold>
                <FoldSummary block={block} />
                <ol>{block.stops.map(renderNote)}</ol>
              </Fold>
            </li>
          ) : (
            renderNote(block.stop)
          )
        )}
      </Timeline>
    </Page>

    <Spread id="artist">
      <div className="text">
        <h2>
          <TitleArt src={TITLES.artist.src} alt={TITLES.artist.alt} $w="min(320px, 70vw)" $tilt={-3} />
        </h2>
        <Notes>
          <ArtistCopy />
        </Notes>
      </div>
      <Taped $tilt={-4} className="art">
        <img className="media" src={NDHAUS_PHOTO} alt="ndhaus holding the original Zeus artwork" />
      </Taped>
    </Spread>

    <Page>
      <h2 className="center">
        <TitleArt src={TITLES.memes.src} alt={TITLES.memes.alt} $w="min(360px, 70vw)" $tilt={-3} />
      </h2>
      <Wall>
        {MEMES_12.map((m, i) => (
          <li key={m.src} style={{ '--t': `${tilt(i)}deg` } as React.CSSProperties}>
            <img src={m.src} alt={m.alt} loading="lazy" />
          </li>
        ))}
      </Wall>
    </Page>

    <Page>
      <h2 className="center">
        <TitleArt src={TITLES.comic.src} alt={TITLES.comic.alt} $w="min(360px, 76vw)" $tilt={2} />
      </h2>
      <p className="note center">{CONTENT.comic.subtitle}</p>
      <ComicStack href="/comic" aria-label="Read the comic">
        {COMIC_PAGES.map((p, i) => (
          <img key={p} src={p} alt={`Comic page ${i + 1}`} loading="lazy" style={{ transform: `rotate(${tilt(i + 1)}deg)` }} />
        ))}
      </ComicStack>
    </Page>

    <Closing>
      <InkRing className="ring" />
      <Buy big />
      <Socials color={C.ink} size={30} />
    </Closing>
    <DrawnFooter color={C.ink} />
  </Sheet>
);

export default RedesignE;

/* --------------------------------------------------------------------------- */

const Sheet = styled.div`
  position: relative;
  font-family: 'Ubuntu', sans-serif;
  color: ${C.ink};
  background-color: ${C.paper};
  background-image: linear-gradient(rgba(61, 141, 245, 0.16) 1.5px, transparent 1.5px),
    linear-gradient(90deg, rgba(61, 141, 245, 0.16) 1.5px, transparent 1.5px);
  background-size: 30px 30px;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: max(28px, calc(50% - 690px));
    width: 6px;
    border-left: 2px solid rgba(244, 93, 179, 0.55);
    border-right: 2px solid rgba(244, 93, 179, 0.55);
    pointer-events: none;
  }
  .center {
    display: flex;
    justify-content: center;
    text-align: center;
  }
  h2 {
    margin: 0 0 26px;
  }
  @media (max-width: 700px) {
    &::before {
      left: 10px;
    }
  }
`;

const Margin = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: max(0px, calc(50% - 735px));
  width: 26px;
  pointer-events: none;
  > * {
    position: absolute;
    left: -2px;
    width: 30px;
    height: auto;
  }
  @media (max-width: 1100px) {
    display: none;
  }
`;

const Hero = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  align-items: center;
  gap: clamp(20px, 4vw, 60px);
  max-width: 1240px;
  margin: 0 auto;
  padding: 10px clamp(24px, 4vw, 40px) 40px;
  .zeus {
    position: relative;
  }
  .zeus img {
    display: block;
    width: 100%;
    max-width: 520px;
    height: auto;
    margin: 0 auto;
    transform-origin: 50% 92%;
    transition: transform 420ms ${EASE_OUT};
  }
  ${hover(css`
    .zeus:hover img {
      transform: rotate(-3deg) scale(1.02);
    }
  `)}
  .sp1 { position: absolute; top: 6%; left: 8%; }
  .sp2 { position: absolute; top: 40%; right: 6%; }
  h1 {
    position: relative;
    isolation: isolate;
    margin: 0;
  }
  .buy {
    margin: -10px 0 24px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
    .zeus img {
      max-width: 320px;
    }
    h1 {
      display: flex;
      justify-content: center;
    }
    .buy {
      margin-top: 10px;
    }
  }
`;

const Swipe = () => (
  <SwipeSvg viewBox="0 0 600 200" preserveAspectRatio="none">
    <path d="M30 130 C 150 80, 320 160, 570 96" fill="none" stroke={C.blue} strokeWidth="96" strokeLinecap="round" opacity="0.9" />
  </SwipeSvg>
);

const SwipeSvg = styled.svg`
  position: absolute;
  left: 4%;
  top: 60%;
  width: 92%;
  height: 46%;
  z-index: -1;
  pointer-events: none;
`;

const Arrow = styled.svg`
  display: block;
  width: 130px;
  height: 74px;
  margin: -6px 0 0 34%;
  @media (max-width: 900px) {
    display: none;
  }
`;

const Taped = styled.figure<{ $tilt: number }>`
  position: relative;
  margin: 0;
  transform: rotate(${(p) => p.$tilt}deg);
  transition: transform 360ms ${EASE_OUT};
  &.wide {
    width: min(760px, 88vw);
    margin: 10px auto 30px;
  }
  &.art {
    justify-self: center;
    width: min(440px, 82vw);
  }
  .media {
    display: block;
    width: 100%;
    height: auto;
    background: ${C.ink};
    border: 3px solid ${C.ink};
  }
  video.media {
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
  &.wide::before,
  &.wide::after {
    content: '';
    position: absolute;
    z-index: 2;
    width: 110px;
    height: 32px;
    background: rgba(247, 224, 60, 0.72);
    border-left: 2px dashed rgba(14, 27, 51, 0.15);
    border-right: 2px dashed rgba(14, 27, 51, 0.15);
    pointer-events: none;
  }
  &::before {
    top: -14px;
    left: -26px;
    transform: rotate(-35deg);
  }
  &::after {
    right: -26px;
    bottom: -14px;
    transform: rotate(-35deg);
  }
  .bolt {
    position: absolute;
    right: -22px;
    top: -38px;
    z-index: 3;
    transform: rotate(16deg);
  }
  ${hover(css`
    &:hover {
      transform: rotate(0deg) translateY(-4px);
    }
  `)}
`;

const Spread = styled.section<{ $flip?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  align-items: center;
  gap: clamp(30px, 5vw, 80px);
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(36px, 5vw, 64px) clamp(24px, 4vw, 40px);
  ${(p) =>
    p.$flip &&
    css`
      grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
      .text {
        order: 2;
      }
    `}
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    .text {
      order: 0;
    }
  }
`;

const Notes = styled.div`
  max-width: 60ch;
  font-size: clamp(17px, 1.3vw, 19px);
  line-height: 30px;
  p {
    margin: 0 0 30px;
  }
  a {
    color: inherit;
    font-weight: 700;
    background: linear-gradient(transparent 58%, rgba(247, 224, 60, 0.9) 58%);
    text-decoration: none;
    transition: background-size 240ms ${EASE_OUT};
  }
`;

const Page = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: clamp(36px, 5vw, 64px) clamp(24px, 4vw, 40px);
  .note {
    max-width: 640px;
    margin: 24px auto 40px;
    font-size: 17px;
    line-height: 1.55;
  }
`;

const Checklist = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 22px 56px;
  max-width: 880px;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  a {
    display: flex;
    gap: 16px;
    min-height: 44px;
    color: inherit;
    text-decoration: none;
  }
  .box {
    display: grid;
    place-items: center;
    flex: none;
    width: 42px;
    height: 42px;
    border: 3px solid ${C.ink};
    border-radius: 6px 9px 5px 10px;
  }
  .t {
    display: inline;
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(21px, 2vw, 28px);
    text-transform: uppercase;
    background: linear-gradient(transparent 55%, ${C.yellow} 55%) no-repeat 0 0 / 0% 100%;
    transition: background-size 320ms ${EASE_OUT};
  }
  .v {
    display: block;
    margin-top: 6px;
    font-size: 17px;
    overflow-wrap: anywhere;
  }
  a:focus-visible .t {
    background-size: 100% 100%;
  }
  ${hover(css`
    a:hover .t {
      background-size: 100% 100%;
    }
  `)}
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Timeline = styled.ol`
  max-width: 980px;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  ol {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  li.fold {
    display: block;
    padding: 24px 0;
  }
  li {
    display: grid;
    grid-template-columns: 250px minmax(0, 1fr);
    gap: 28px;
    padding: 18px 0;
    border-bottom: 2px dashed rgba(14, 27, 51, 0.18);
  }
  .date {
    position: relative;
    align-self: start;
    padding: 12px 18px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 20px;
    text-align: center;
  }
  .ring {
    position: absolute;
    inset: -4px -6px;
    width: calc(100% + 12px);
    height: calc(100% + 8px);
    transition: transform 320ms ${EASE_OUT};
  }
  ${hover(css`
    li:hover .ring {
      transform: rotate(-4deg) scale(1.05);
    }
  `)}
  h3 {
    margin: 4px 0 6px;
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: 21px;
  }
  .pill {
    display: inline-block;
    margin-bottom: 6px;
    padding: 2px 12px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 14px;
    background: ${C.pink};
    color: ${C.white};
    border: 2.5px solid ${C.ink};
    border-radius: 999px;
    transform: rotate(-3deg);
  }
  p {
    margin: 0;
    font-size: 17px;
    line-height: 1.6;
  }
  li.in-progress .date {
    font-size: 24px;
  }
  li.in-progress .date::before {
    content: '';
    position: absolute;
    inset: 6px 10px;
    z-index: -1;
    background: ${C.yellow};
    border-radius: 40%;
  }
  li.in-progress h3 {
    font-size: 26px;
  }
  .date {
    isolation: isolate;
  }
  a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: inherit;
    font-weight: 700;
  }
  @media (max-width: 700px) {
    li {
      grid-template-columns: 1fr;
      gap: 8px;
    }
    .date {
      justify-self: start;
    }
  }
`;

const Wall = styled.ul`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: clamp(18px, 3vw, 42px);
  max-width: 1080px;
  margin: 0 auto;
  padding: 0;
  list-style: none;
  li {
    position: relative;
    transform: rotate(var(--t));
    transition: transform 280ms ${EASE_OUT};
  }
  li::before {
    content: '';
    position: absolute;
    top: -12px;
    left: 50%;
    z-index: 1;
    width: 80px;
    height: 26px;
    background: rgba(247, 224, 60, 0.72);
    transform: translateX(-50%) rotate(-4deg);
  }
  img {
    display: block;
    width: 100%;
    aspect-ratio: 1;
    object-fit: cover;
    border: 3px solid ${C.ink};
  }
  ${hover(css`
    li:hover {
      z-index: 2;
      transform: rotate(0deg) scale(1.12) translateY(-6px);
    }
  `)}
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ComicStack = styled.a`
  display: flex;
  justify-content: center;
  gap: 18px;
  img {
    width: min(250px, 28vw);
    height: auto;
    border: 3px solid ${C.ink};
    transition: transform 300ms ${EASE_OUT};
  }
  ${hover(css`
    &:hover img {
      transform: rotate(0deg) translateY(-8px) !important;
    }
  `)}
`;

const Closing = styled.section`
  position: relative;
  display: grid;
  justify-items: center;
  gap: 20px;
  width: min(560px, 90vw);
  margin: 20px auto 30px;
  padding: 60px 20px;
  .ring {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
`;
